/**
 * Set or clear the manual firm window for a part the ERP does not schedule.
 *
 * Customer admins only (the backend refuses everyone else). The value governs
 * until the ERP starts sending a window for the part — then the ERP wins and
 * this value is kept on the row as "superseded".
 */
import { useEffect, useState } from 'react';
import { Loader2, CalendarClock, RotateCcw } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { purchasingAPI } from '../../lib/api';
import type { PartNotFromErp } from '../../lib/api';

interface ManualFirmDaysDialogProps {
  open: boolean;
  part: PartNotFromErp | null;
  onClose: () => void;
  /** Called with the saved row so the table can update in place. */
  onSaved: (row: PartNotFromErp) => void;
}

export default function ManualFirmDaysDialog({ open, part, onClose, onSaved }: ManualFirmDaysDialogProps) {
  const [days, setDays] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // A refused save stays on screen until the next attempt — a toast alone
  // vanishes and leaves the dialog looking as if nothing happened.
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDays(part?.manual_schedule_firm != null ? String(part.manual_schedule_firm) : '');
      setSaveError(null);
    }
  }, [open, part?.part_number, part?.manual_schedule_firm]);

  if (!part) return null;

  const parsed = Number.parseInt(days, 10);
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 3650;
  const hasManual = part.manual_schedule_firm != null;

  const submit = async (value: number | null) => {
    setSubmitting(true);
    setSaveError(null);
    try {
      const res = await purchasingAPI.setPartManualFirm({
        part_number: part.part_number,
        manual_schedule_firm: value,
        customer_id: part.customer,
      });
      toast.success(value === null
        ? `Firm days for ${part.part_number} reset to the default (${res.data.default_firm_days ?? '—'}).`
        : `Firm days for ${part.part_number} set to ${value}.`);
      onSaved(res.data);
      onClose();
    } catch (err: unknown) {
      console.error('Manual firm days save failed:', err);
      const axiosError = err as { response?: { data?: { error?: string; detail?: string } } };
      const message = axiosError.response?.data?.error || axiosError.response?.data?.detail || 'Failed to save firm days';
      setSaveError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-sky-700" />
            Firm days — {part.part_number}
          </DialogTitle>
          <DialogDescription>
            {part.description || 'No description'}{part.suppliers.length ? <> · {part.suppliers.join(', ')}</> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            The ERP does not send a firm window for this part, so it runs on the tenant default of{' '}
            <span className="font-medium text-foreground">{part.default_firm_days ?? '—'} days</span>.
            A value set here applies instead — until the ERP starts sending one, which then takes over.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="manual-firm-days">Firm days</Label>
            <Input
              id="manual-firm-days"
              type="number"
              min={1}
              max={3650}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder={String(part.default_firm_days ?? '')}
              disabled={submitting}
            />
            {days !== '' && !valid && (
              <p className="text-xs text-red-600">Enter a whole number of days between 1 and 3650.</p>
            )}
          </div>
          {hasManual && (
            <p className="text-xs text-muted-foreground">
              Currently {part.manual_schedule_firm} days, set by {part.manual_set_by_name || 'a customer admin'}.
            </p>
          )}
          {saveError && (
            <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {saveError}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {hasManual && (
            <Button variant="outline" onClick={() => submit(null)} disabled={submitting} type="button" className="mr-auto">
              <RotateCcw className="h-4 w-4 mr-2" />Use default
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={submitting} type="button">Cancel</Button>
          <Button onClick={() => submit(parsed)} disabled={submitting || !valid} type="button">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CalendarClock className="h-4 w-4 mr-2" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
